/**
 * Admin → Rewards → Gift Card Fulfilment (SHOPPER-REWARDS-1A · MANUAL_ADMIN).
 * Operators buy cards in the supplier portal, then attach them here. Codes/PINs are never listed.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Gift, RefreshCw, Search, ShieldAlert, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmt, when, errText, label, StatusChip, SlaBadge } from "@/components/admin/giftcards/shared";
import { OrderDetailSheet } from "@/components/admin/giftcards/OrderDetailSheet";
import { OperatorsPanel } from "@/components/admin/giftcards/OperatorsPanel";
import { BrandOfferLinks } from "@/components/admin/giftcards/BrandOfferLinks";
import { BrandStoreMatching } from "@/components/admin/giftcards/BrandStoreMatching";
import { WaitlistPanel } from "@/components/admin/giftcards/WaitlistPanel";

const QUEUES = [
  ["open", "All open"], ["new", "New"], ["awaiting", "Awaiting fulfilment"], ["processing", "Processing"],
  ["due_soon", "Due within 6h"], ["overdue", "Overdue"], ["needs_review", "Needs review"],
  ["fulfilled", "Fulfilled"], ["failed", "Failed"], ["restored", "Cashback restored"], ["all", "All"],
];

function ConfigBanner({ cfg }) {
  if (!cfg) return null;
  const on = cfg.redemption_enabled;
  return (
    <div className={`pf-card p-4 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs ${on ? "border-cyan-200" : "border-amber-200 bg-amber-50/40"}`} data-testid="gc-config-banner">
      <span className="font-semibold text-slate-900" data-testid="gc-config-redemption">Shopper redemption: {on ? "ON" : "OFF"}{cfg.preview_environment ? " (preview)" : ""}</span>
      <span>Method: <b>{cfg.fulfilment_method ? label(cfg.fulfilment_method) : "None"}</b></span>
      <span>Minimum available cashback: <b>{cfg.min_redemption_minor ? fmt(cfg.min_redemption_minor) : "not configured"}</b></span>
      <span>Delivery commitment: <b>{cfg.delivery_sla_hours}h</b></span>
      <span>Second approval: <b>{cfg.second_approval_threshold_minor ? `≥ ${fmt(cfg.second_approval_threshold_minor)}` : "off"}</b></span>
      {!cfg.vault_configured && <span className="text-rose-700 font-semibold inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" />Secure card storage not configured</span>}
      {!cfg.viewer_can_fulfil && <span className="text-slate-500">You have view-only access.</span>}
    </div>
  );
}

function QueueTabs({ queue, counts, onPick }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="gc-queue-tabs">
      {QUEUES.map(([k, label]) => (
        <button key={k} onClick={() => onPick(k)} data-testid={`gc-queue-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${queue === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"} ${k === "overdue" && counts?.overdue ? "ring-1 ring-rose-300" : ""}`}>
          {label} <span className="tabular-nums opacity-70">{counts?.[k] ?? "·"}</span>
        </button>
      ))}
    </div>
  );
}

function OrdersTable({ items, onOpen }) {
  if (!items.length) return <div className="text-sm text-slate-500 text-center py-10" data-testid="gc-orders-empty">No orders in this queue.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[860px]" data-testid="gc-orders-table">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
            {["Order", "Shopper", "Brand", "Value", "Reserved", "Requested", "Deliver by", "Status", "Assigned", "Supplier ref"].map((h) => (
              <th key={h} className="py-2 pr-3 font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r.id)} className="border-b border-slate-50 hover:bg-cyan-50/40 cursor-pointer" data-testid={`gc-order-row-${r.id}`}>
              <td className="py-2 pr-3 font-mono text-xs">{r.order_ref}</td>
              <td className="py-2 pr-3 font-mono text-xs">{r.shopper_reference}</td>
              <td className="py-2 pr-3 text-slate-900 font-medium">{r.brand}</td>
              <td className="py-2 pr-3 tabular-nums">{r.value_minor != null ? fmt(r.value_minor, r.currency) : r.value_display}</td>
              <td className="py-2 pr-3 tabular-nums">{fmt(r.reserved_minor)}</td>
              <td className="py-2 pr-3 text-xs text-slate-500">{when(r.requested_at)}</td>
              <td className="py-2 pr-3 text-xs"><div className="text-slate-600">{when(r.delivery_due_at)}</div><SlaBadge row={r} testid={`gc-order-sla-${r.id}`} /></td>
              <td className="py-2 pr-3"><StatusChip status={r.status} testid={`gc-order-status-${r.id}`} />{r.flags.length > 0 && <div className="text-[10px] text-rose-700 font-bold mt-1">{r.flags.join(", ")}</div>}</td>
              <td className="py-2 pr-3 text-xs text-slate-600">{r.assigned_to_name || "—"}</td>
              <td className="py-2 pr-3 text-xs font-mono text-slate-600">{r.supplier_order_ref || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function GiftCardFulfilment() {
  const [params, setParams] = useSearchParams();
  const [queue, setQueue] = useState("open");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  useEffect(() => { const t = setTimeout(() => setQ(qInput.trim()), 350); return () => clearTimeout(t); }, [qInput]);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const orderId = params.get("order");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [o, c, f] = await Promise.all([
        api.get("/admin/rewards/fulfilment/orders", { params: { queue, q: q || undefined } }),
        api.get("/admin/rewards/fulfilment/queues"),
        api.get("/admin/rewards/fulfilment/config"),
      ]);
      setItems(o.data.items || []); setCounts(c.data.counts); setCfg(f.data);
    } catch (e) { toast.error(errText(e, "Could not load the fulfilment queue")); }
    finally { setLoading(false); }
  }, [queue, q]);
  useEffect(() => { reload(); }, [reload]);

  const openOrder = (id) => setParams(id ? { order: id } : {});

  return (
    <div className="p-6 space-y-5" data-testid="admin-gift-card-fulfilment">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin/rewards" className="text-xs text-slate-500 flex w-fit items-center gap-1 hover:text-slate-800" data-testid="gc-back-to-rewards"><ArrowLeft className="w-3 h-3" /> Rewards</Link>
          <div className="pf-chip pf-chip-cyan w-fit mt-3"><Gift className="w-3 h-3" /> Manual fulfilment</div>
          <h1 className="font-display text-2xl md:text-3xl text-slate-900 mt-2">Gift Card Fulfilment</h1>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">Buy the card in the approved supplier portal, then attach it to the order. Every card must reach the shopper within 24 hours of the request.</p>
        </div>
        <div className="flex gap-2 items-center">
          <label className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Order, shopper ref, brand…" aria-label="Search gift card orders"
                   className="pl-8 pr-3 py-1.5 rounded-full border border-slate-200 text-xs w-56" data-testid="gc-search" />
          </label>
          <button onClick={reload} className="px-3 py-1.5 rounded-full border border-slate-200 text-xs hover:bg-slate-50 inline-flex items-center gap-2" data-testid="gc-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>
      <ConfigBanner cfg={cfg} />
      <QueueTabs queue={queue} counts={counts} onPick={setQueue} />
      <div className="pf-card p-4"><OrdersTable items={items} onOpen={openOrder} /></div>
      <OperatorsPanel isSuperAdmin={cfg?.viewer_role === "super_admin"} />
      <BrandOfferLinks canEdit={!!cfg?.viewer_can_fulfil} />
      <BrandStoreMatching canEdit={!!cfg?.viewer_can_fulfil} />
      <WaitlistPanel />
      <OrderDetailSheet orderId={orderId} onClose={() => openOrder(null)} onChanged={reload} />
    </div>
  );
}
